import clsx from 'clsx';

import { getIconSize } from './Button';
import styles from './Button.module.css';
import type { ButtonProps } from './Button.types';

/**
 * A skeleton button component. It can be used to show that the initial render of a button is loading,
 * useful if the props of a button are only known after an async operation.
 *
 * Don't use this component to show the loading state of a button that is already rendered - prefer using loadable buttons instead.
 */
export function ButtonSkeleton(
    props: Pick<
        ButtonProps,
        'label' | 'leadingIcon' | 'size' | 'rounded' | 'trailingIcon' | 'iconOnly' | 'className'
    >
) {
    const {
        size = 'medium',
        label,
        leadingIcon,
        rounded,
        trailingIcon,
        iconOnly,
        className,
    } = props;

    const buttonStyles = clsx(
        styles.button,
        [styles[size]],
        {
            [styles.rounded]: rounded,
        },
        'shimmer',
        className
    );

    const iconSize = getIconSize({ size, iconOnly });

    return (
        <div aria-busy className={buttonStyles}>
            {leadingIcon ? (
                <span style={{ width: iconSize, height: iconSize }} aria-hidden />
            ) : null}
            {!iconOnly ? (
                <>
                    <span className={styles.skeleton} aria-hidden>
                        {label}
                    </span>
                    {trailingIcon ? (
                        <span style={{ width: iconSize, height: iconSize }} aria-hidden />
                    ) : null}
                </>
            ) : null}
        </div>
    );
}
